/**
 * image-rs 0.25 `imageops::resize(FilterType::Triangle)` 精确移植
 * （vertical_sample → f32 中间缓冲 → horizontal_sample，最终 FloatNearest 舍入 + clamp）。
 * 用于渲染合成全分辨率放大，与桌面版像素级对拍。
 *
 * 约定：src/dst 为行优先 packed 数组，channels ∈ {1, 3, 4}。
 */

function triangleKernel(x: number): number {
  const ax = Math.abs(x);
  return ax < 1 ? 1 - ax : 0;
}

/** f32::round（half away from zero）+ clamp 到 u8 */
function quantize(v: number): number {
  const r = Math.round(v); // 正值域内与 f32::round 一致
  return r < 0 ? 0 : r > 255 ? 255 : r;
}

/** 计算某一输出坐标处的源采样窗口与权重（对拍 sample.rs:259-297） */
function windowWeights(
  outPos: number,
  srcLen: number,
  dstLen: number,
): { left: number; weights: number[] } {
  const ratio = srcLen / dstLen;
  const sratio = ratio < 1 ? 1 : ratio;
  const srcSupport = 1.0 * sratio; // triangle support = 1.0
  const inputPos = (outPos + 0.5) * ratio;
  let left = Math.floor(inputPos - srcSupport);
  left = Math.min(Math.max(left, 0), srcLen - 1);
  let right = Math.ceil(inputPos + srcSupport);
  right = Math.min(Math.max(right, left + 1), srcLen);
  const center = inputPos - 0.5;
  const weights: number[] = [];
  let sum = 0;
  for (let i = left; i < right; i++) {
    const w = triangleKernel((i - center) / sratio);
    weights.push(w);
    sum += w;
  }
  for (let k = 0; k < weights.length; k++) weights[k] /= sum;
  return { left, weights };
}

/**
 * Triangle 重采样。先垂直后水平，中间不降精度（f32）。
 * src: srcW×srcH，dst: dstW×dstH。
 */
export function resizeTriangle(
  src: Uint8Array | Uint8ClampedArray,
  srcW: number,
  srcH: number,
  dstW: number,
  dstH: number,
  channels: 1 | 3 | 4,
): Uint8Array {
  if (srcW === dstW && srcH === dstH) return Uint8Array.from(src);

  // 垂直：srcH → dstH，输出 f32 (srcW × dstH × ch)
  const mid = new Float32Array(srcW * dstH * channels);
  for (let outy = 0; dstH > 0 && outy < dstH; outy++) {
    const { left, weights } = windowWeights(outy, srcH, dstH);
    for (let x = 0; x < srcW; x++) {
      for (let c = 0; c < channels; c++) {
        let t = 0;
        for (let k = 0; k < weights.length; k++) {
          t += src[((left + k) * srcW + x) * channels + c] * weights[k];
        }
        mid[(outy * srcW + x) * channels + c] = t;
      }
    }
  }

  // 水平：srcW → dstW，量化
  const out = new Uint8Array(dstW * dstH * channels);
  for (let outx = 0; dstW > 0 && outx < dstW; outx++) {
    const { left, weights } = windowWeights(outx, srcW, dstW);
    for (let y = 0; y < dstH; y++) {
      for (let c = 0; c < channels; c++) {
        let t = 0;
        for (let k = 0; k < weights.length; k++) {
          t += mid[(y * srcW + left + k) * channels + c] * weights[k];
        }
        out[(y * dstW + outx) * channels + c] = quantize(t);
      }
    }
  }
  return out;
}
